/**
 * PolicyListContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import PolicyListPresenter from './PolicyListPresenter';
import {
  useGetProvisions,
  useCreateProvision,
  useUpdateProvision,
} from 'hooks/ProvisionHooks';
import { useGetCoupons } from 'hooks/CouponHooks';
import { useGetGrades } from 'hooks/GradeHooks';
import { useUserStack } from 'store';
import MessageAlert from 'utils';
import { SettingApi } from 'api';
import { LoadingPage } from 'components';

// setting_id
// admin_id
// parcel_service_setting: 정기배송 정책 관리 설정
// rental_setting: 렌탈 정책 관리 설정
const initialSetting = {
  setting_id: null,
  // 정기배송 정책
  parcel_service_setting: {
    policy: '',
    stop_delivery_deadline: null, // 배송 지연/정지 요청 기한
    change_delivery_deadline: null, // 배송정보 변경 가능 기한
  },
  // 렌탈상품 정책
  rental_setting: {
    policy: '',
    delay_inspection_deadline: null, // 점검기한 연기
    renewal_agreement_deadline: null, // 약정일 갱신 설정
  },
  // 렌탈상품 정책
  stamp_setting: {
    guide: '', // 스탬프 안내사항
    expired_date: 90, //스탬프 유효기간
    goal_num: 10, // 스탬프 목표갯수
    dessert_standard: 999999, // 디저트 스탬프 기준 금액
    md_standard: 999999, // MD 상품 스탬프 기준 금액
    coupon_id: null, //쿠폰PK
  },
  // 렌탈상품 정책
  app_setting: {
    android: {
      latestVersion: '',
      minVersion: '',
      isInspected: false,
      inspectedMessage: '점검중입니다.\n잠시 후 다시 시도해주세요.',
    },
    ios: {
      latestVersion: '',
      minVersion: '',
      isInspected: false,
      inspectedMessage: '점검중입니다.\n잠시 후 다시 시도해주세요.',
    },
  },
};

/**----------------------------------------
 * [Component] 정책관리: 컨테이너
 * ----------------------------------------
 */
const PolicyListContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== MUTATE ===== */
  const userInfo = useUserStack();
  const { createProvision } = useCreateProvision();
  const { updateProvision } = useUpdateProvision();
  const { data: Provisions, refetch } = useGetProvisions();
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [isLoading, setIsLoading] = useState(false);
  const [provisionData, setProvisionData] = useState([]);
  const [setting, setSetting] = useState(initialSetting);
  const [selectedKey, setSelectedKey] = useState('parcel_service_setting'); // [parcel_service_setting, rental_setting, provision]
  //쿠폰 조회
  const { data: Coupons } = useGetCoupons({ payment_type: 'STAMP' });
  //등급조회
  const { data: Grades } = useGetGrades();
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**--------------------
   * 데이터 바인딩
   * --------------------
   */
  const handleChange = (key1, key2, val, key3) => {
    setSetting((p) => ({
      ...p,
      [key1]: {
        ...p[key1],
        // [key2]: val,
        ...(key3
          ? {
              [key2]: {
                ...p[key1]?.[key2],
                [key3]: val,
              },
            }
          : { [key2]: val }),
      },
    }));
  };
  /**--------------------
   * 정책 저장
   * --------------------
   */
  const handleSaveSetting = async () => {
    setIsLoading(true);
    try {
      const {
        parcel_service_setting,
        rental_setting,
        stamp_setting,
        point_setting,
        grade_setting,
        payment_setting,
        app_setting,
      } = setting;
      let newData;

      // 정기배송 정책 저장
      if (selectedKey === 'parcel_service_setting') {
        newData = {
          admin_id: setting.aadmin_id,
          setting_id: setting.setting_id,
          parcel_service_setting,
        };
      }
      // 렌탈상품 정책 저장
      else if (selectedKey === 'rental_setting') {
        newData = {
          admin_id: setting.aadmin_id,
          setting_id: setting.setting_id,
          rental_setting,
        };
      }
      // 스탬프 정책 저장
      else if (selectedKey === 'stamp_setting') {
        newData = {
          admin_id: setting.aadmin_id,
          setting_id: setting.setting_id,
          stamp_setting,
        };
      }
      // 포인트 정책 저장
      else if (selectedKey === 'point_setting') {
        const newPointSetting = Object.entries(point_setting || {}).reduce(
          (acc, [key, value]) => {
            acc[key] = value ?? 0;
            return acc;
          },
          {}
        );
        newData = {
          admin_id: setting.aadmin_id,
          setting_id: setting.setting_id,
          point_setting: newPointSetting,
        };
      }
      // 등급 정책 저장
      else if (selectedKey === 'grade_setting') {
        newData = {
          admin_id: setting.aadmin_id,
          setting_id: setting.setting_id,
          grade_setting,
        };
      }
      // 결제 정책 저장
      else if (selectedKey === 'payment_setting') {
        for (const value of Object.values(payment_setting)) {
          if (value === '' || value === null || value === undefined) {
            MessageAlert.info('필수 입력값을 입력하지 않았습니다.');
            setIsLoading(false);
            return;
          }
        }
        newData = {
          admin_id: setting.aadmin_id,
          setting_id: setting.setting_id,
          payment_setting,
        };
      } else if (selectedKey === 'app_setting') {
        newData = {
          admin_id: setting.aadmin_id,
          setting_id: setting.setting_id,
          app_setting,
        };
      }

      console.log('저장데이터', newData);
      const { status, message } = await SettingApi.updateSetting(newData);
      if (status !== 200) throw { message };

      setTimeout(() => {
        MessageAlert.info('저장되었습니다');
        setIsLoading(false);
      }, 450);
    } catch (e) {
      setIsLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 설정정보 조회
   * --------------------
   */
  const getSetting = () =>
    SettingApi.getSettings({ admin_id: 2 })
      .then(({ status, message, data }) => {
        if (status !== 200) throw { message };
        const d = data.filter((item) => item.admin_id === userInfo.admin_id)[0];
        // console.log('d: ', d);

        setSetting({
          admin_id: userInfo.admin_id,
          setting_id: d.setting_id,
          parcel_service_setting: d.parcel_service_setting
            ? d.parcel_service_setting
            : initialSetting.parcel_service_setting,
          rental_setting: d.rental_setting
            ? d.rental_setting
            : initialSetting.rental_setting,
          stamp_setting: d.stamp_setting
            ? d.stamp_setting
            : initialSetting.stamp_setting,
          point_setting: d.point_setting
            ? d.point_setting
            : initialSetting.point_setting,
          grade_setting: d.grade_setting
            ? d.grade_setting
            : initialSetting.grade_setting,
          payment_setting: d.payment_setting
            ? d.payment_setting
            : initialSetting.payment_setting,
          app_setting: d.app_setting
            ? d.app_setting
            : initialSetting.app_setting,
        });
      })
      .catch((e) => MessageAlert.error(e.message));

  const getProvisionTitle = (type) => {
    switch (type) {
      case 'PRIVACY':
        return '개인정보 처리방침';
      case 'USE':
        return '서비스 이용약관';
      case 'PRESENT':
        return '선물하기 정책';
      case 'LOSS':
        return '분실 약관';
      case 'HOW_TO_USE':
        return '이용안내';
      case 'CHANGE_POINT':
        return '잔돈 포인트 약관';
      case 'CHARGE':
        return '충전식 상품권 약관';
      case 'LOCATION':
        return '위치정보 약관';
      case 'REFUND':
        return '환불 약관';
      default:
        return '약관';
    }
  };

  /**--------------------
   * 내용 변경 핸들러
   * --------------------
   */
  const handleContentChange = (type, content) => {
    const updatedData = provisionData.map((item) =>
      item.provision_type === type
        ? { ...item, provision_content: content }
        : item
    );

    // 데이터가 없는 경우 새로운 항목 추가
    if (!provisionData.some((item) => item.provision_type === type)) {
      updatedData.push({
        provision_id: null,
        provision_type: type,
        provision_title: getProvisionTitle(type),
        provision_content: content,
      });
    }

    setProvisionData(updatedData);
  };

  /**--------------------
   * 저장 버튼 클릭 시 호출
   * --------------------
   */
  const handleSave = async () => {
    setIsLoading(true);

    // 초기 데이터와 현재 데이터를 비교하여 변경 여부 확인
    const hasChanges = provisionData.some((item) => {
      const originalItem = Provisions?.data?.find(
        (provision) => provision.provision_type === item.provision_type
      );

      // 기존 항목이 없거나 내용이 변경된 경우 true 반환
      return (
        !originalItem ||
        originalItem.provision_content !== item.provision_content
      );
    });

    // 변경 사항이 없을 경우 사용자에게 알림 표시 후 종료
    if (!hasChanges) {
      MessageAlert.info('바뀐 내용이 없습니다.');
      setIsLoading(false);
      return;
    }

    try {
      await Promise.all(
        provisionData.map((item) => {
          if (item.provision_id) {
            // 기존 약관 업데이트
            return updateProvision({
              provision_id: item.provision_id,
              provision_content: item.provision_content,
            });
          } else {
            // 새로운 약관 생성
            return createProvision({
              provision_title: item.provision_title,
              provision_content: item.provision_content,
              provision_type: item.provision_type,
            });
          }
        })
      );
      MessageAlert.success('저장되었습니다.');
      refetch();
    } catch (error) {
      MessageAlert.error('저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  /* ===== HOOKS ===== */
  /**--------------------
   * 데이터 초기화
   * --------------------
   */
  useEffect(() => {
    const newData = Provisions?.data;
    setProvisionData(newData);
    // eslint-disable-next-line
  }, [Provisions]);

  useEffect(() => {
    getSetting();
  }, []);
  console.log('setting', setting);
  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <PolicyListPresenter
        provisionData={provisionData}
        userInfo={userInfo}
        onContentChange={handleContentChange}
        onSave={handleSave}
        onSaveSetting={handleSaveSetting}
        onChange={handleChange}
        setting={setting}
        coupons={Coupons?.data}
        grades={Grades?.data}
        // selectedKey={selectedKey}
        setSelectedKey={setSelectedKey}
      />
    </>
  );
};

export default PolicyListContainer;
